import React,{ createContext,useEffect,useRef,useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ITicketStore } from './interfaces/ITicketStore';
import type { TicketStatus } from '../types/tickets/types';

const currentDate = new Date();
currentDate.setHours(23, 59, 59, 999);
const oneWeekAgo = new Date(currentDate);
oneWeekAgo.setDate(currentDate.getDate() - 7);
oneWeekAgo.setHours(0, 0, 0, 0);

export type statusType = TicketStatus;

export type ticketStatusType = statusType | 'null' | 'all';

export type PermissionRule =
  "responsible"
  | "responsibleTeamSuperOnly"
  | "responsibleTeamOnly"
  | "teamSuperOnly"
  | "team"
  | "everyone"
  | null;

export type orderType =
  'inicio'
  | 'usuarioNome'
  | 'nome'
  | 'responsavel'
  | 'prioridade'
  | 'novo'
  | 'dataPrevisao'
  | 'status';

export type TicketNotificationEvent =
  "opened"
  | "accepted"
  | "refused"
  | "inProgress"
  | "completed"
  | "cancelled"
  | "closed"
  | "reopened"
  | "deadlineReached"
  | "markedLate"
  | "priorityChanged"
  | "newInteraction";

export type NotificationRecipient =
  "team"
  | "teamSupervisor"
  | "responsible"
  | "requester";

export type filtrosType = {
  duplicado: boolean | null;
  userEmissor: { label: string, value: string }[] | null,
  expirado: boolean | null | string,
  modelos: { label: string, value: number }[] | null
  modelosNovos: { label: string, value: string }[] | null,
  inicioRange: {
    de: number | null,
    ate: number | null,
  } | null,
  previsaoRange: {
    de: number | null,
    ate: number | null
  } | null,
  status?: ticketStatusType[] | null,
  clientes: { label: string, value: string }[] | null,
  locais: { label: string, value: string }[] | null,
  conjuntos: { label: string, value: string }[] | null,
  unidades: { label: string, value: string }[] | null,
  ordenarPor: string,
  ordem: boolean | null,
  responsavel: { label: string, value: string }[] | null,
} | null;

const initalState = {
  emitidos: false,
  setEmitidos: () => { },
  ordem: false,
  setOrdem: () => { },
  ordenarPor: 'inicio',
  setOrdenarPor: () => { },
  userEmissor: null,
  setUserEmissor: () => { },
  modelosNovos: null,
  setModelosNovos: () => { },
  modelos: null,
  setModelos: () => { },
  inicioRangeDe: oneWeekAgo.getTime(),
  setInicioRangeDe: () => { },
  inicioRangeAte: currentDate.getTime(),
  setInicioRangeAte: () => { },
  previsaoRangeDe: oneWeekAgo.getTime(),
  setPrevisaoRangeDe: () => { },
  previsaoRangeAte: currentDate.getTime(),
  setPrevisaoRangeAte: () => { },
  clientes: null,
  setClientes: () => { },
  locais: null,
  setLocais: () => { },
  conjuntos: null,
  setConjuntos: () => { },
  unidades: null,
  setUnidades: () => { },
  expirado: 'null',
  setExpirado: () => { },
  status: ['pendente', 'aceito', 'emandamento', 'concluido', 'recusado', 'cancelado', 'fechado', 'fechadoCancelamento', 'fechadoRecusa', 'reaberto', 'aprovado'] as ticketStatusType[],
  setStatus: () => { },
  filter: null,
  setDefaultValues: () => { },
  responsavel: null,
  setResponsavel: () => { },
  exibeDuplicados: null,
  setExibeDuplicados: () => { }
};

export const TicketsContext = createContext<ITicketStore>(initalState);
export default function TicketsProvider({ children }: { children: React.ReactNode }) {
  const [emitidos, setEmitidos] = useState(false);
  const [ordem, setOrdem] = useState<boolean>(false);
  const { t } = useTranslation('translation');
  const [ordenarPor, setOrdenarPor] = useState<string>('inicio');
  const [userEmissor, setUserEmissor] = useState<{ label: string, value: string }[] | null>(null);
  const [modelosNovos, setModelosNovos] = useState<{ label: string, value: string }[] | null>(null);
  const [modelos, setModelos] = useState<{ label: string, value: number }[] | null>(null);
  const [inicioRangeDe, setInicioRangeDe] = useState<Date | number>(oneWeekAgo.getTime());
  const [inicioRangeAte, setInicioRangeAte] = useState<Date | number>(currentDate.getTime());
  const [previsaoRangeDe, setPrevisaoRangeDe] = useState<Date | number | null>(null);
  const [previsaoRangeAte, setPrevisaoRangeAte] = useState<Date | number | null>(null);
  const [clientes, setClientes] = useState<{ label: string, value: string }[] | null>(null);
  const [locais, setLocais] = useState<{ label: string, value: string }[] | null>(null);
  const [conjuntos, setConjuntos] = useState<{ label: string, value: string }[] | null>(null);
  const [unidades, setUnidades] = useState<{ label: string, value: string }[] | null>(null);
  const [expirado, setExpirado] = useState<string>('null');
  const [status, setStatus] = useState<ticketStatusType[] | null>(null);
  const [responsavel, setResponsavel] = useState<{ label: string, value: string }[] | null>(null);
  const [exibeDuplicados, setExibeDuplicados] = useState<boolean | null>(null);

  const filterRef = useRef({} as filtrosType);

  useEffect(() => {
    const recovered = localStorage.getItem('filtros');
    if (recovered) {
      filterRef.current = JSON.parse(recovered);
      filterRef.current?.ordenarPor && setOrdenarPor(filterRef.current.ordenarPor);
      filterRef.current?.userEmissor && setUserEmissor((filterRef.current?.userEmissor as { value: string, label: string }[])?.map((ele) => ele));
      filterRef.current?.modelosNovos && setModelosNovos((filterRef.current?.modelosNovos as { value: string, label: string }[])?.map((ele) => ele));
      filterRef.current?.modelos && setModelos((filterRef.current?.modelos as { value: number, label: string }[])?.map((ele) => ele));
      filterRef.current?.inicioRange?.de && setInicioRangeDe(new Date(filterRef.current?.inicioRange?.de).setHours(0, 0, 0, 0));
      filterRef.current?.inicioRange?.ate && setInicioRangeAte(new Date(filterRef.current?.inicioRange?.ate).setHours(23, 59, 59, 999));
      filterRef.current?.previsaoRange?.de && setPrevisaoRangeDe(new Date(filterRef.current?.previsaoRange?.de).setHours(0, 0, 0, 0));
      filterRef.current?.previsaoRange?.ate && setPrevisaoRangeAte(new Date(filterRef.current?.previsaoRange?.ate).setHours(23, 59, 59, 999));
      filterRef.current?.clientes && setClientes((filterRef.current?.clientes as { value: string, label: string }[])?.map((ele) => ele));
      filterRef.current?.locais && setLocais((filterRef.current?.locais as { value: string, label: string }[])?.map((ele) => ele));
      filterRef.current?.conjuntos && setConjuntos((filterRef.current?.conjuntos as { value: string, label: string }[])?.map((ele) => ele));
      filterRef.current?.unidades && setUnidades((filterRef.current?.unidades as { value: string, label: string }[])?.map((ele) => ele));
      filterRef.current?.expirado && setExpirado(filterRef?.current?.expirado as string ?? null);
      filterRef.current?.status && setStatus([...filterRef.current?.status as ticketStatusType[]]);
      filterRef.current?.responsavel && setResponsavel((filterRef.current?.responsavel as { value: string, label: string }[])?.map((ele) => ele));
      filterRef.current?.duplicado && setExibeDuplicados(filterRef?.current?.duplicado);
    }
  }, [t]);

  const setDefaultValues = () => {
    setOrdenarPor('inicio');
    setUserEmissor(null);
    setModelosNovos(null);
    setModelos(null);
    setInicioRangeDe(oneWeekAgo.getTime());
    setInicioRangeAte(currentDate.getTime());
    setPrevisaoRangeDe(null);
    setPrevisaoRangeAte(null);
    setClientes(null);
    setLocais(null);
    setConjuntos(null);
    setUnidades(null);
    setExpirado('null');
    setStatus(null);
  };

  const store = {
    setDefaultValues,
    emitidos,
    setEmitidos,
    ordem,
    setOrdem,
    ordenarPor,
    setOrdenarPor,
    userEmissor,
    setUserEmissor,
    modelosNovos,
    setModelosNovos,
    modelos,
    setModelos,
    inicioRangeDe,
    setInicioRangeDe,
    inicioRangeAte,
    setInicioRangeAte,
    previsaoRangeDe,
    setPrevisaoRangeDe,
    previsaoRangeAte,
    setPrevisaoRangeAte,
    clientes,
    setClientes,
    locais,
    setLocais,
    conjuntos,
    setConjuntos,
    unidades,
    setUnidades,
    expirado,
    setExpirado,
    status,
    setStatus,
    filterRef,
    responsavel,
    setResponsavel,
    exibeDuplicados,
    setExibeDuplicados,
  };

  return (
    <TicketsContext.Provider value={store}>
      {children}
    </TicketsContext.Provider>
  );
}
